// Iter 202 — Betway Bet Coach page
//
// Two main flows:
//   1. List view → user's open slips (manual + scraped), each with a
//      compact card showing combined-probability, recommendation,
//      per-leg traffic lights.
//   2. Detail view → full breakdown with expand-on-click per leg,
//      cashout EV gauge, safer-parlay swap suggestions.
//
// Also:
//   - "Add slip manually" form (paste legs)
//   - "Sync from Betway" button (uses backend Playwright scraper)
//   - "Update cashout offer" inline editor
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { TopNav } from "../components/TopNav";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  PlusCircle, Trash2, RefreshCw, ArrowLeft, ChevronDown, ChevronRight,
  Wallet, AlertTriangle, CheckCircle2, XCircle, HelpCircle, Sparkles,
  TrendingUp, TrendingDown, Coins, Shield, Activity, Zap,
} from "lucide-react";
import { API } from "../App";


// ───────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────

const TLIGHT_META = {
  green:   { color: "text-emerald-500", bg: "bg-emerald-500/10 border-emerald-500/40", icon: CheckCircle2, label: "KEEP" },
  yellow:  { color: "text-amber-500",   bg: "bg-amber-500/10 border-amber-500/40",     icon: AlertTriangle, label: "WATCH" },
  red:     { color: "text-rose-500",    bg: "bg-rose-500/10 border-rose-500/40",       icon: XCircle, label: "DROP" },
  unknown: { color: "text-zinc-400",    bg: "bg-zinc-500/10 border-zinc-500/40",       icon: HelpCircle, label: "WATCH" },
};

const REC_META = {
  "CASH OUT": { color: "text-emerald-500", bg: "bg-emerald-500/15 border-emerald-500/50", icon: Coins },
  "HOLD": { color: "text-sky-500", bg: "bg-sky-500/15 border-sky-500/50", icon: Shield },
  "PARTIAL CASHOUT": { color: "text-amber-500", bg: "bg-amber-500/15 border-amber-500/50", icon: AlertTriangle },
  "PARTIAL CASHOUT (if available)": { color: "text-amber-500", bg: "bg-amber-500/15 border-amber-500/50", icon: AlertTriangle },
  "DROP RED LEGS": { color: "text-rose-500", bg: "bg-rose-500/15 border-rose-500/50", icon: Trash2 },
  "WATCH": { color: "text-zinc-400", bg: "bg-zinc-500/15 border-zinc-500/50", icon: Activity },
};


const fmtMoney = (n, currency = "USD") => {
  if (n == null || isNaN(n)) return "—";
  return `${currency} ${Number(n).toFixed(2)}`;
};

const fmtPct = (n, digits = 1) => (n == null || isNaN(n)) ? "—" : `${Number(n).toFixed(digits)}%`;


// ───────────────────────────────────────────────────────────────────────
// Leg card (collapsed → traffic light + expand to detail)
// ───────────────────────────────────────────────────────────────────────

const LegCard = ({ leg }) => {
  const [open, setOpen] = useState(false);
  const meta = TLIGHT_META[leg.traffic_light] || TLIGHT_META.unknown;
  const Icon = meta.icon;
  return (
    <div className={`rounded-lg border ${meta.bg} transition`} data-testid={`leg-${leg.leg_index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-3"
        data-testid={`leg-toggle-${leg.leg_index}`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Icon className={`w-5 h-5 shrink-0 ${meta.color}`} />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold truncate">
              {leg.home_team} <span className="text-muted-foreground">vs</span> {leg.away_team}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              {leg.market} · {leg.selection} · {leg.odds?.toFixed?.(2)}
              {leg.live_minute != null && (
                <span className="ml-2 text-rose-500 font-semibold">
                  {leg.live_minute}&apos; {leg.live_score?.home}-{leg.live_score?.away}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className={`${meta.bg} ${meta.color} border text-[10px] uppercase tracking-[0.16em]`}>
            {meta.label}
          </Badge>
          {leg.current_probability_pct != null && (
            <span className={`text-sm font-bold tabular-nums ${meta.color}`}>
              {fmtPct(leg.current_probability_pct, 0)}
            </span>
          )}
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-border/60 pt-2.5">
          <div className="grid grid-cols-3 gap-2 text-[10px]">
            <div>
              <div className="uppercase tracking-[0.16em] text-muted-foreground">Live prob</div>
              <div className={`text-sm font-bold tabular-nums ${meta.color}`}>
                {fmtPct(leg.current_probability_pct)}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-[0.16em] text-muted-foreground">Implied</div>
              <div className="text-sm font-bold tabular-nums">
                {fmtPct(leg.implied_probability_pct)}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-[0.16em] text-muted-foreground">Edge</div>
              <div className={`text-sm font-bold tabular-nums ${
                leg.edge_pp != null ? (leg.edge_pp >= 0 ? "text-emerald-500" : "text-rose-500") : ""
              }`}>
                {leg.edge_pp != null ? `${leg.edge_pp >= 0 ? "+" : ""}${leg.edge_pp.toFixed(1)}pp` : "—"}
              </div>
            </div>
          </div>
          <div className="text-xs text-foreground italic font-medium">{leg.verdict}</div>
          {leg.reasoning?.length > 0 && (
            <ul className="space-y-1 text-[11px] text-muted-foreground">
              {leg.reasoning.map((r, i) => (
                <li key={i} className="flex gap-1.5">
                  <span className="text-rose-500">·</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          )}
          {leg.fixture_id && (
            <Link to={`/live-ai/${leg.fixture_id}`}
                  className="inline-flex items-center gap-1 text-[11px] text-rose-500 hover:underline"
                  data-testid={`leg-open-live-${leg.leg_index}`}>
              Open live AI breakdown <ChevronRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────────
// Slip card (list view)
// ───────────────────────────────────────────────────────────────────────

const SlipCard = ({ slip, onDelete }) => {
  const ev = slip.evaluation || {};
  const rec = ev.recommendation || "WATCH";
  const recMeta = REC_META[rec] || REC_META.WATCH;
  const RecIcon = recMeta.icon;
  const isParlay = (ev.legs?.length || 0) > 1;
  return (
    <Card className={`overflow-hidden border-l-2 ${
      ev.risk_summary?.red >= 1 ? "border-l-rose-500" :
      ev.risk_summary?.yellow >= 1 ? "border-l-amber-500" :
      "border-l-emerald-500"
    } hover:shadow-[0_0_20px_rgba(244,63,94,0.15)] transition`}
    data-testid={`slip-${slip.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {slip.source === "betway_scraper" ? "Betway · auto-fetched" : "Manual entry"}
              {" · "}{ev.legs?.length || 0} leg{(ev.legs?.length || 0) !== 1 && "s"}
              {isParlay && <span className="ml-2 text-rose-500 font-bold">PARLAY</span>}
            </div>
            <div className="text-sm font-bold mt-0.5">
              {fmtMoney(slip.stake, slip.stake_currency)} → potential {fmtMoney(slip.potential_payout, slip.stake_currency)}
            </div>
          </div>
          <Badge className={`${recMeta.bg} border ${recMeta.color} uppercase tracking-[0.16em] text-[10px] gap-1`}>
            <RecIcon className="w-3 h-3" />
            {rec}
          </Badge>
        </div>

        <div className="space-y-1.5">
          {(ev.legs || []).map(leg => <LegCard key={leg.leg_index} leg={leg} />)}
        </div>

        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Live</div>
            <div className={`text-sm font-bold tabular-nums ${
              ev.combined_probability_pct != null && ev.combined_probability_pct >= 50
                ? "text-emerald-500" : "text-rose-500"
            }`}>
              {fmtPct(ev.combined_probability_pct)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Placed</div>
            <div className="text-sm font-bold tabular-nums text-muted-foreground">
              {fmtPct(ev.placed_implied_probability_pct)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Fair cashout</div>
            <div className="text-sm font-bold tabular-nums">
              {fmtMoney(ev.fair_value_cashout, slip.stake_currency)}
            </div>
          </div>
        </div>

        {ev.rationale && (
          <p className="text-[11px] text-muted-foreground italic border-l-2 border-rose-500/40 pl-2">
            {ev.rationale}
          </p>
        )}

        <div className="flex items-center justify-between pt-1">
          <Link to={`/coach/${slip.id}`} className="text-[11px] text-rose-500 hover:underline font-semibold"
                data-testid={`slip-open-${slip.id}`}>
            Open detail →
          </Link>
          <button onClick={() => onDelete(slip.id)}
                  className="text-[11px] text-muted-foreground hover:text-rose-500 transition flex items-center gap-1"
                  data-testid={`slip-delete-${slip.id}`}>
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      </CardContent>
    </Card>
  );
};


// ───────────────────────────────────────────────────────────────────────
// Add slip form
// ───────────────────────────────────────────────────────────────────────

const blankLeg = () => ({ home_team: "", away_team: "", market: "1X2", selection: "Home", odds: "" });

const AddSlipForm = ({ onCreated }) => {
  const [stake, setStake] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [cashout, setCashout] = useState("");
  const [legs, setLegs] = useState([blankLeg()]);
  const [busy, setBusy] = useState(false);

  const updateLeg = (i, key, val) => {
    setLegs(prev => prev.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  };

  const submit = async () => {
    const validLegs = legs.filter(l => l.home_team && l.away_team && l.odds);
    if (!stake || validLegs.length === 0) {
      toast.error("Provide stake and at least one complete leg.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/coach/slips`, {
        stake: Number(stake),
        stake_currency: currency,
        cashout_offered: cashout ? Number(cashout) : null,
        legs: validLegs.map(l => ({
          home_team: l.home_team.trim(),
          away_team: l.away_team.trim(),
          market: l.market.trim(),
          selection: l.selection.trim(),
          odds: Number(l.odds),
        })),
      });
      toast.success("Slip added — running the coach engine…");
      setStake(""); setCashout(""); setLegs([blankLeg()]);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add slip.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="add-slip-form">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <PlusCircle className="w-4 h-4 text-rose-500" />
          <span className="text-xs uppercase tracking-[0.18em] font-bold">Add a slip manually</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Stake</Label>
            <Input type="number" step="0.01" value={stake} onChange={e => setStake(e.target.value)}
                   placeholder="100.00" data-testid="stake-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Currency</Label>
            <Input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())}
                   placeholder="USD" data-testid="currency-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Cashout offer (optional)</Label>
            <Input type="number" step="0.01" value={cashout} onChange={e => setCashout(e.target.value)}
                   placeholder="—" data-testid="cashout-offer-input" />
          </div>
        </div>

        <div className="space-y-2">
          {legs.map((leg, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-end" data-testid={`leg-row-${i}`}>
              <div className="col-span-3">
                <Label className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Home</Label>
                <Input value={leg.home_team} onChange={e => updateLeg(i, "home_team", e.target.value)}
                       placeholder="Brazil" data-testid={`leg-home-${i}`} />
              </div>
              <div className="col-span-3">
                <Label className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Away</Label>
                <Input value={leg.away_team} onChange={e => updateLeg(i, "away_team", e.target.value)}
                       placeholder="Egypt" data-testid={`leg-away-${i}`} />
              </div>
              <div className="col-span-2">
                <Label className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Market</Label>
                <Input value={leg.market} onChange={e => updateLeg(i, "market", e.target.value)}
                       placeholder="1X2 / BTTS / Over 2.5" data-testid={`leg-market-${i}`} />
              </div>
              <div className="col-span-2">
                <Label className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Selection</Label>
                <Input value={leg.selection} onChange={e => updateLeg(i, "selection", e.target.value)}
                       placeholder="Home / Yes" data-testid={`leg-sel-${i}`} />
              </div>
              <div className="col-span-1">
                <Label className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Odds</Label>
                <Input type="number" step="0.01" value={leg.odds} onChange={e => updateLeg(i, "odds", e.target.value)}
                       placeholder="1.85" data-testid={`leg-odds-${i}`} />
              </div>
              <div className="col-span-1 flex justify-end">
                {legs.length > 1 && (
                  <button onClick={() => setLegs(prev => prev.filter((_, idx) => idx !== i))}
                          className="text-rose-500 hover:text-rose-400 transition pb-2"
                          data-testid={`leg-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setLegs(prev => [...prev, blankLeg()])}
                  data-testid="add-leg-btn">
            <PlusCircle className="w-4 h-4 mr-1" /> Add another leg
          </Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="submit-slip-btn">
            {busy ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Coach this slip
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// ───────────────────────────────────────────────────────────────────────
// Detail view
// ───────────────────────────────────────────────────────────────────────

const DetailView = ({ slipId }) => {
  const navigate = useNavigate();
  const [slip, setSlip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cashoutInput, setCashoutInput] = useState("");
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/coach/slips/${slipId}`);
      setSlip(r.data);
      if (r.data?.evaluation?.offered_cashout != null) {
        setCashoutInput(String(r.data.evaluation.offered_cashout));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load slip.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await load();
    };
    tick();
    pollRef.current = setInterval(tick, 8000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [slipId]);

  const updateCashoutOffer = async () => {
    if (!cashoutInput) return;
    setBusy(true);
    try {
      await axios.post(`${API}/coach/slips/${slipId}/cashout-offer`,
        { cashout_offered: Number(cashoutInput) });
      toast.success("Cashout offer updated.");
      await load();
    } catch (e) {
      toast.error("Failed.");
    } finally {
      setBusy(false);
    }
  };

  const takeCashout = async () => {
    if (!slip?.evaluation?.offered_cashout) {
      toast.error("Enter the cashout offer first.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/coach/slips/${slipId}/cashout`,
        { cashout_taken: slip.evaluation.offered_cashout });
      toast.success("Marked as cashed out.");
      navigate("/coach");
    } catch (e) {
      toast.error("Failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!slip) {
    return <div className="text-center text-muted-foreground py-12">Loading slip…</div>;
  }
  const ev = slip.evaluation || {};
  const rec = ev.recommendation || "WATCH";
  const recMeta = REC_META[rec] || REC_META.WATCH;
  const RecIcon = recMeta.icon;
  const swaps = slip.safer_parlay_swaps || [];

  return (
    <div className="space-y-5" data-testid="slip-detail">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/coach")} data-testid="back-to-coach-list">
          <ArrowLeft className="w-4 h-4 mr-1" /> All slips
        </Button>
        <Badge className={`${recMeta.bg} border ${recMeta.color} text-xs uppercase tracking-[0.18em] gap-1 px-3 py-1.5`}>
          <RecIcon className="w-4 h-4" /> {rec}
        </Badge>
      </div>

      {/* Hero card */}
      <Card className="overflow-hidden border-rose-500/30">
        <div className="px-5 py-4 bg-gradient-to-br from-rose-500/10 via-transparent to-sky-500/10 border-b border-border">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {slip.source === "betway_scraper" ? "Auto-fetched from Betway" : "Manual entry"}
            {" · "}{ev.legs?.length || 0} legs
          </div>
          <div className="text-2xl font-black mt-1 tabular-nums">
            {fmtMoney(slip.stake, slip.stake_currency)} → {fmtMoney(slip.potential_payout, slip.stake_currency)}
          </div>
        </div>
        <CardContent className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Live combined</div>
              <div className={`text-2xl font-black tabular-nums ${
                ev.combined_probability_pct >= 50 ? "text-emerald-500" : "text-rose-500"
              }`} data-testid="combined-prob">
                {fmtPct(ev.combined_probability_pct)}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-muted/30 border border-border">
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">At placement</div>
              <div className="text-2xl font-black tabular-nums text-muted-foreground">
                {fmtPct(ev.placed_implied_probability_pct)}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Fair-value cashout</div>
              <div className="text-2xl font-black text-amber-500 tabular-nums" data-testid="fair-cashout">
                {fmtMoney(ev.fair_value_cashout, slip.stake_currency)}
              </div>
            </div>
          </div>
          {ev.rationale && (
            <p className="text-sm text-muted-foreground italic border-l-2 border-rose-500/40 pl-3">
              {ev.rationale}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Cashout EV calculator */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-sky-500" />
            <span className="text-xs uppercase tracking-[0.18em] font-bold">Cashout value tracker</span>
          </div>
          <div className="grid grid-cols-3 gap-2 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Betway offers</Label>
              <Input type="number" step="0.01" value={cashoutInput} onChange={e => setCashoutInput(e.target.value)}
                     placeholder="e.g. 580" data-testid="cashout-input" />
            </div>
            <Button size="sm" onClick={updateCashoutOffer} disabled={busy} data-testid="update-cashout-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Update
            </Button>
            <Button size="sm" variant="outline" onClick={takeCashout} disabled={busy || !ev.offered_cashout}
                    data-testid="take-cashout-btn">
              <Coins className="w-4 h-4 mr-1" /> Take cashout
            </Button>
          </div>
          {ev.offered_cashout != null && ev.cashout_ev_pct != null && (
            <div className={`p-3 rounded-lg border ${
              ev.cashout_ev_pct > 5 ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-500" :
              ev.cashout_ev_pct < -8 ? "bg-rose-500/10 border-rose-500/40 text-rose-500" :
              "bg-amber-500/10 border-amber-500/40 text-amber-500"
            }`} data-testid="cashout-ev-card">
              <div className="text-[10px] uppercase tracking-[0.16em] opacity-70 mb-1">Cashout EV vs fair value</div>
              <div className="text-2xl font-black tabular-nums flex items-center gap-2">
                {ev.cashout_ev_pct > 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                {ev.cashout_ev_pct >= 0 ? "+" : ""}{ev.cashout_ev_pct.toFixed(1)}%
              </div>
              <div className="text-xs mt-1 opacity-90">
                Betway: {fmtMoney(ev.offered_cashout, slip.stake_currency)}
                {" · "}fair: {fmtMoney(ev.fair_value_cashout, slip.stake_currency)}
                {" · "}delta: {ev.cashout_ev_delta >= 0 ? "+" : ""}{fmtMoney(ev.cashout_ev_delta, slip.stake_currency)}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-leg breakdown */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-rose-500" />
            <span className="text-xs uppercase tracking-[0.18em] font-bold">
              Per-leg verdict ({ev.legs?.length})
              <span className="ml-2 text-[10px]">
                <span className="text-emerald-500">{ev.risk_summary?.green || 0} 🟢</span>{" · "}
                <span className="text-amber-500">{ev.risk_summary?.yellow || 0} 🟡</span>{" · "}
                <span className="text-rose-500">{ev.risk_summary?.red || 0} 🔴</span>{" · "}
                <span className="text-zinc-400">{ev.risk_summary?.unknown || 0} ?</span>
              </span>
            </span>
          </div>
          <div className="space-y-2">
            {(ev.legs || []).map(leg => <LegCard key={leg.leg_index} leg={leg} />)}
          </div>
        </CardContent>
      </Card>

      {/* Safer-parlay swaps */}
      {swaps.length > 0 && (
        <Card className="border-amber-500/40 bg-gradient-to-br from-amber-500/5 via-transparent to-rose-500/5"
              data-testid="safer-parlay-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold">
                Safer-parlay swap suggestions ({swaps.length})
              </span>
            </div>
            {swaps.map((sw, i) => (
              <div key={i} className="rounded-lg border border-border bg-background/50 p-3 space-y-2"
                   data-testid={`swap-${i}`}>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-xs">
                    <div className="text-[9px] uppercase tracking-[0.16em] text-rose-500">REMOVE</div>
                    <div className="font-bold">
                      {sw.swap_out.home_team} vs {sw.swap_out.away_team}
                    </div>
                    <div className="text-muted-foreground">
                      {sw.swap_out.market} · {sw.swap_out.selection} @ {sw.swap_out.odds?.toFixed?.(2)}
                    </div>
                    <div className="text-rose-500 mt-0.5">
                      Live: {fmtPct(sw.swap_out.current_probability_pct, 0)}
                    </div>
                  </div>
                  <div className="text-xs border-l border-border pl-3">
                    <div className="text-[9px] uppercase tracking-[0.16em] text-emerald-500">REPLACE WITH</div>
                    <div className="font-bold">
                      {sw.swap_in.home_team} vs {sw.swap_in.away_team}
                    </div>
                    <div className="text-muted-foreground">
                      {sw.swap_in.market} · {sw.swap_in.selection} @ {sw.swap_in.odds?.toFixed?.(2)}
                    </div>
                    <div className="text-emerald-500 mt-0.5">
                      Confidence: {fmtPct(sw.swap_in.confidence_pct, 0)}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground italic">{sw.rationale}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────────
// Upcoming events tab
// ───────────────────────────────────────────────────────────────────────

const UpcomingTab = ({ day }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const r = await axios.get(`${API}/coach/upcoming?day=${day}`, { timeout: 200000 });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Upcoming fetch failed.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [day]);

  if (loading) {
    return (
      <div className="text-center text-muted-foreground py-12" data-testid="upcoming-loading">
        <Activity className="w-10 h-10 mx-auto mb-3 animate-pulse opacity-40" />
        <p>Loading Betway upcoming events… (this can take up to 2 min)</p>
      </div>
    );
  }
  if (!data || data.count === 0) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-muted-foreground" data-testid="upcoming-empty">
          <Wallet className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-semibold text-foreground mb-1">No upcoming events found for {day}.</p>
          <p className="text-xs">{data?.scraper_message || ""}</p>
        </CardContent>
      </Card>
    );
  }

  // Group by league
  const byLeague = {};
  for (const e of data.events) {
    const lg = e.league || "Unknown league";
    (byLeague[lg] = byLeague[lg] || []).push(e);
  }

  return (
    <div className="space-y-4" data-testid={`upcoming-tab-${day}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {data.count} events from Betway · {day === "today" ? "Today" : "Tomorrow"} · {data.date}
      </div>
      {Object.entries(byLeague).map(([lg, events]) => (
        <Card key={lg}>
          <CardContent className="p-4 space-y-2">
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-rose-500">{lg}</div>
            {events.map((e, i) => {
              const p = e.prediction;
              return (
                <div key={i} className="rounded-lg border border-border bg-muted/20 px-3 py-2 flex items-center justify-between gap-3"
                     data-testid={`upcoming-row-${day}-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold truncate">
                      {e.home_team} <span className="text-muted-foreground">vs</span> {e.away_team}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {e.kickoff_text}
                      {e.odds_1x2 && <span className="ml-2">· Betway odds: H {e.odds_1x2[0]} / D {e.odds_1x2[1]} / A {e.odds_1x2[2]}</span>}
                    </div>
                  </div>
                  {p ? (
                    <div className="text-right shrink-0">
                      <div className="text-[9px] uppercase tracking-[0.16em] text-emerald-500 font-bold">NDAKUBET</div>
                      <div className="text-sm font-bold truncate">{p.ml_pick || p.market || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {p.confidence_pct != null && `${Number(p.confidence_pct).toFixed(0)}% conf`}
                        {p.predicted_score && ` · ${p.predicted_score}`}
                      </div>
                      {p.match_id && (
                        <Link to={`/live-ai/${p.match_id}`} className="text-[10px] text-rose-500 hover:underline" data-testid={`upcoming-link-${i}`}>
                          Live AI →
                        </Link>
                      )}
                    </div>
                  ) : (
                    <div className="text-right shrink-0">
                      <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">No NDAKUBET pred</div>
                      <div className="text-[10px] text-muted-foreground">Outside our schedule today</div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────────
// Hit-rate tracker tab
// ───────────────────────────────────────────────────────────────────────

const HitRateTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/coach/hit-rate`);
        if (!cancelled) setData(r.data);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-center text-muted-foreground py-12">Loading…</div>;
  if (!data) return <div className="text-center text-muted-foreground py-12">No data.</div>;
  const s = data.summary || {};

  return (
    <div className="space-y-5" data-testid="hit-rate-tab">
      <Card className="border-emerald-500/30">
        <CardContent className="p-5 space-y-3">
          <div className="text-xs uppercase tracking-[0.18em] font-bold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500" /> Coach hit-rate (self-learning)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Settled slips" value={s.total_settled_slips} />
            <Stat label="Win rate" value={`${s.win_rate_pct ?? 0}%`} accent="emerald" />
            <Stat label="ROI" value={`${s.roi_pct ?? 0}%`} accent={s.roi_pct >= 0 ? "emerald" : "rose"} />
            <Stat label="Net P/L" value={`${s.roi_currency || ""} ${s.roi_net ?? 0}`} accent={s.roi_net >= 0 ? "emerald" : "rose"} />
          </div>
        </CardContent>
      </Card>

      {(data.by_market || []).length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] font-bold">Per-market hit rate</div>
            <div className="space-y-1.5">
              {data.by_market.map(m => (
                <div key={m.market} className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-semibold flex-1 truncate">{m.market}</span>
                  <span className="text-muted-foreground tabular-nums">{m.won}/{m.total}</span>
                  <div className="w-28 h-1.5 bg-muted rounded overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${m.hit_rate_pct}%` }} />
                  </div>
                  <span className="tabular-nums w-12 text-right text-emerald-500 font-bold">{m.hit_rate_pct}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(data.recent || []).length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-2">
            <div className="text-xs uppercase tracking-[0.18em] font-bold">Recent settled slips</div>
            <div className="space-y-1.5 text-xs">
              {data.recent.map(r => (
                <div key={r.id} className="flex items-center justify-between gap-2 py-1 border-b border-border last:border-0">
                  <span className="text-muted-foreground">{r.settled_at?.slice(0, 16).replace("T", " ")}</span>
                  <span className="flex-1 truncate">{r.n_legs}-leg · {r.last_recommendation || "—"}</span>
                  <span className="tabular-nums">{r.currency} {r.stake}</span>
                  <Badge className={`text-[10px] ${
                    r.outcome === "won" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" :
                    r.outcome === "lost" ? "bg-rose-500/15 text-rose-500 border-rose-500/40" :
                    "bg-amber-500/15 text-amber-500 border-amber-500/40"
                  } border uppercase`}>{r.outcome}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(data.summary?.total_settled_slips ?? 0) === 0 && (
        <p className="text-center text-xs text-muted-foreground">
          Self-learning data is collected as slips settle.  Sync from Betway, let matches finish, and the tracker fills up automatically.
        </p>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent = "" }) => (
  <div className="p-3 rounded-lg bg-muted/30 border border-border">
    <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
    <div className={`text-xl font-black tabular-nums ${
      accent === "emerald" ? "text-emerald-500" : accent === "rose" ? "text-rose-500" : ""
    }`}>{value ?? "—"}</div>
  </div>
);


// ───────────────────────────────────────────────────────────────────────
// List view (with tabs)
// ───────────────────────────────────────────────────────────────────────

const ListView = () => {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/coach/slips`);
      setSlips(r.data?.slips || []);
    } catch {
      // ignore transient errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await load();
    };
    tick();
    pollRef.current = setInterval(tick, 12000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const onSync = async () => {
    setSyncing(true);
    toast.message("Logging into Betway…", { description: "This can take up to 30 seconds." });
    try {
      const r = await axios.post(`${API}/coach/sync-from-betway`);
      if (r.data?.ok) {
        toast.success(`Synced ${r.data.newly_synced} new slip(s) from Betway.`);
      } else {
        toast.error(r.data?.message || "Betway sync failed.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API}/coach/slips/${id}`);
      toast.success("Slip removed.");
      await load();
    } catch {
      toast.error("Failed to remove.");
    }
  };

  const [tab, setTab] = useState("slips");

  return (
    <div className="space-y-5" data-testid="coach-list">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-rose-500 font-bold flex items-center gap-2">
            <Zap className="w-3 h-3" /> Betway Bet Coach
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mt-1">Cashout or hold?</h1>
          <p className="text-sm text-muted-foreground max-w-xl mt-1">
            Drop your open Betway slips here — I&apos;ll evaluate every leg with the live AI engine,
            grade your cashout offer against fair value, and suggest safer replacements when needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "slips" && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowForm(s => !s)} data-testid="toggle-add-form">
                <PlusCircle className="w-4 h-4 mr-1" /> {showForm ? "Hide form" : "Add slip"}
              </Button>
              <Button size="sm" onClick={onSync} disabled={syncing} data-testid="sync-betway-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync from Betway"}
              </Button>
            </>
          )}
        </div>
      </header>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border" data-testid="coach-tabs">
        {[
          {id: "slips", label: "My slips"},
          {id: "today", label: "Upcoming · Today"},
          {id: "tomorrow", label: "Upcoming · Tomorrow"},
          {id: "hits", label: "Hit tracker"},
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`coach-tab-${t.id}`}
            className={`px-4 py-2 text-xs uppercase tracking-[0.16em] font-bold border-b-2 transition ${
              tab === t.id ? "border-rose-500 text-rose-500" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "today" && <UpcomingTab day="today" />}
      {tab === "tomorrow" && <UpcomingTab day="tomorrow" />}
      {tab === "hits" && <HitRateTab />}

      {tab === "slips" && (
        <>
          {showForm && <AddSlipForm onCreated={() => { setShowForm(false); load(); }} />}

          {loading ? (
            <div className="text-center text-muted-foreground py-12">
              <Activity className="w-10 h-10 mx-auto mb-3 animate-pulse opacity-40" />
              <p>Loading slips…</p>
            </div>
          ) : slips.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center text-muted-foreground" data-testid="empty-state">
                <Wallet className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-semibold text-foreground mb-1">No slips to coach yet</p>
                <p className="text-sm">Add one manually or hit &quot;Sync from Betway&quot; to auto-fetch your open bets.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-4">
              {slips.map(s => <SlipCard key={s.id} slip={s} onDelete={onDelete} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────────
// Page entry
// ───────────────────────────────────────────────────────────────────────

const CoachPage = () => {
  const { slipId } = useParams();
  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {slipId ? <DetailView slipId={slipId} /> : <ListView />}
      </main>
    </div>
  );
};

export default CoachPage;
